import { useEffect, useState } from 'react';
import getAllPostsFromAPI from './getAllPostsFromAPI';
import moment from 'moment';
import { DATE_MOMENT_FORMAT } from '../../constants/dateFormat';
import { defaultTotals, getFiltersInitialState } from '../initialStates';
import { POSTS_LIMIT_PER_REQUEST } from '../constants';

export type AuthenticatedAccountPostsParams = {
    perPage: number;
    startIndex: number;
    domainObject: {
        clientId: number;
        accountIds: string[];
        mediaTypeIds: string[];
        startDate: string;
        endDate: string;
        orderByMap: any;
        content: string;
        groupCarouselPosts: boolean;
    };
};

export const getInitialQueryParams = (
    clientId,
): AuthenticatedAccountPostsParams => {
    const initialQueryParams = getFiltersInitialState();

    return {
        perPage: POSTS_LIMIT_PER_REQUEST,
        startIndex: 0,
        domainObject: {
            clientId: clientId,
            groupCarouselPosts: true,
            accountIds: null,
            mediaTypeIds: null,
            // "yyyy-MM-ddTHH:mm:ss.sssZ" for moment
            startDate: moment(initialQueryParams.startDate.value).format(
                DATE_MOMENT_FORMAT,
            ),
            endDate: moment(initialQueryParams.endDate.value).format(
                DATE_MOMENT_FORMAT,
            ),
            orderByMap: null,
            content: null,
        },
    };
};

const useAllPosts = clientId => {
    const [query, setQuery] = useState(getInitialQueryParams(clientId));
    const [lastPostIndexRequested, setLastPostIndexRequested] = useState(0); // index are 0 based
    const [postsData, setPostsData] = useState({
        posts: [],
        postCount: 0,
        totals: defaultTotals,
        isLoading: true,
        isAddingPage: false,
    });

    const loadMorePosts = () => {
        const newQueries = { ...query };
        newQueries.startIndex = lastPostIndexRequested + 1;
        setQuery(newQueries);
        fetchPosts(newQueries, false);
        setLastPostIndexRequested(lastIdx => lastIdx + POSTS_LIMIT_PER_REQUEST);
    };

    const setAllPostsQueryParams = params => {
        params.startIndex = 0;
        setQuery(params);
        setLastPostIndexRequested(POSTS_LIMIT_PER_REQUEST - 1);
        fetchPosts(params);
    };

    const fetchPosts = (params, shouldResetPosts = true) => {
        setPostsData({
            ...postsData,
            isLoading: shouldResetPosts,
            isAddingPage: !shouldResetPosts,
        });
        (async () => {
            try {
                const queryParams = {
                    ...params,
                    domainObject: {
                        ...params.domainObject,
                        groupCarouselPosts: true,
                    },
                };
                const {
                    posts,
                    engagementsTotal,
                    videoViewsTotal,
                    impressionsTotal,
                    fmvTotal,
                    postCount,
                } = await getAllPostsFromAPI(queryParams, clientId);

                const totals = {
                    engagementsTotal,
                    videoViewsTotal,
                    impressionsTotal,
                    fmvTotal,
                };
                const newPostData = {
                    posts: shouldResetPosts
                        ? Object.values(posts)
                        : [...postsData.posts, ...Object.values(posts)],
                    totals,
                    postCount,
                    isLoading: false,
                    isAddingPage: false,
                };
                setPostsData(newPostData);
            } catch (e) {
                setPostsData({
                    posts: [],
                    postCount: 0,
                    totals: defaultTotals,
                    isLoading: false,
                    isAddingPage: false,
                });
            }
        })();
    };

    useEffect(() => {
        fetchPosts(query);
        setLastPostIndexRequested(POSTS_LIMIT_PER_REQUEST - 1);
    }, []);

    return {
        ...postsData,
        queryParams: query,
        setAllPostsQueryParams,
        loadMorePosts,
    };
};

export default useAllPosts;
